import type { JSX } from 'react';

import type { Pannello } from './data/pannelli.ts';
import { usePannello, useDecisione } from './usePannello.ts';

/**
 * Il pannello di lavoro di un'area: rende i comandi dichiarati in
 * data/pannelli.ts. Non conosce nessuna area in particolare — aggiungere un
 * pannello non tocca questo file.
 */
export function PannelloLavoro({ pannello }: { readonly pannello: Pannello }): JSX.Element {
  const { controlli } = usePannello(pannello);
  const d = useDecisione();

  return (
    <section className="uc-pannello">
      <header>
        <div>
          <h2>{pannello.titolo}</h2>
          <p>{pannello.nota}</p>
        </div>
      </header>

      <div className="uc-pannello-corpo">
        <div className="uc-comandi">
          {controlli.map((c) => (
            <div className="uc-comando" key={c.id}>
              {c.tipo === 'interruttore' && (
                <label className="uc-scatola uc-cliccabile">
                  <input type="checkbox" checked={c.acceso} onChange={c.onCambia} />
                  <span>
                    <span className="uc-comando-nome">{c.nome}</span>
                    <span className="uc-comando-nota">{c.nota}</span>
                    <span className="uc-esito" style={{ color: c.coloreEsito }}>{c.esito}</span>
                  </span>
                </label>
              )}

              {c.tipo === 'scelta' && (
                <div className="uc-scatola">
                  <label className="uc-comando-nome" htmlFor={c.id}>{c.nome}</label>
                  <div className="uc-comando-nota">{c.nota}</div>
                  <select id={c.id} className="uc-sel" value={c.valore as string} onChange={c.onCambia}>
                    {c.opzioni?.map((o) => <option key={o.valore} value={o.valore}>{o.nome}</option>)}
                  </select>
                  <div className="uc-esito" style={{ color: c.coloreEsito }}>{c.esito}</div>
                </div>
              )}

              {c.tipo === 'cursore' && (
                <div className="uc-scatola">
                  <div className="uc-riga-titolo">
                    <label className="uc-comando-nome" htmlFor={c.id}>{c.nome}</label>
                    <span className="uc-valore mono">{c.etichettaValore}</span>
                  </div>
                  <div className="uc-comando-nota">{c.nota}</div>
                  <input id={c.id} type="range" min={c.min} max={c.max} step={c.passo}
                         value={c.valore as number} onChange={c.onCambia} />
                  <div className="uc-esito" style={{ color: c.coloreEsito }}>{c.esito}</div>
                </div>
              )}

              {c.tipo === 'passi' && (
                <div className="uc-scatola">
                  <div className="uc-riga-titolo">
                    <span className="uc-comando-nome">{c.nome}</span>
                    <span className="mono uc-avanzamento-testo">{c.avanzamento}</span>
                  </div>
                  <div className="uc-comando-nota">{c.nota}</div>
                  <div className="uc-barra"><div style={{ width: c.larghezza }} /></div>
                  <div className="uc-passi">
                    {c.passi?.map((p) => (
                      <label key={p.nome} className="uc-passo">
                        <input type="checkbox" checked={p.fatto} onChange={p.onCambia} />
                        <span style={{ color: p.colore }}>{p.nome}</span>
                      </label>
                    ))}
                  </div>
                  <div className="uc-esito" style={{ color: c.coloreEsito }}>{c.esito}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {pannello.decisione && (
          <div className="uc-decisione">
            <div className="uc-decisione-titolo">{pannello.decisione.titolo}</div>
            <p className="uc-decisione-nota">{pannello.decisione.nota}</p>

            <div className="uc-opzioni">
              {pannello.decisione.opzioni.map(([nome, nota], i) => (
                <label key={nome} className={d.scelta === i ? 'uc-opzione sel' : 'uc-opzione'}>
                  <input type="radio" name="uc-decisione" checked={d.scelta === i} onChange={() => { d.scegli(i); }} />
                  <span>
                    <span className="uc-comando-nome">{nome}</span>
                    <span className="uc-comando-nota">{nota}</span>
                  </span>
                </label>
              ))}
            </div>

            <label className="uc-etichetta-campo" htmlFor="uc-motivazione">
              Motivazione — entra nel fascicolo
            </label>
            <textarea id="uc-motivazione" rows={3} value={d.motivazione}
                      onChange={(e) => { d.scrivi(e.target.value); }}
                      placeholder="Perché hai deciso così. Chi verificherà l'atto leggerà questo testo, non la scelta." />

            <div className="uc-riga-registra">
              {/* disabled, non un click che non fa nulla: l'operatore deve
                  vedere che manca qualcosa, e il testo accanto dice cosa. */}
              <button type="button" className="uc-btn ia" onClick={d.registra} disabled={!d.valida || d.registrata}>
                {d.registrata ? 'Decisione registrata' : 'Registra la decisione'}
              </button>
              <span style={{ color: d.coloreStato }}>{d.stato}</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
